/**
 * Judges, their assignments, and their scores.
 *
 * Two decisions shape this file:
 *
 * - **Assignment is scoped to session × category, never to the event** (D15). "The judges are
 *   hand-picked each day for each category; they will not necessarily be the same 5 judges all
 *   weekend." A different panel every day is the normal path here, not a workaround.
 * - **Scores originate offline.** The id is a client-generated ULID so a retried submission
 *   upserts rather than duplicating, and `version` supports server-side conflict detection. A
 *   locked score is never silently overwritten; changing one requires an audited override.
 */

import { sql } from "drizzle-orm";
import { index, integer, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { archivable, audited, flag, id, instant, ref } from "./columns.js";

/**
 * The community's judges, persisting across sessions, days and years — so "hand-picked each day"
 * means re-selecting familiar names rather than retyping them (D15).
 */
export const judgeRoster = sqliteTable(
  "judge_roster",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    userId: ref("user_id").notNull(),
    displayName: text("display_name").notNull(),
    notes: text("notes"),
    active: flag("active").notNull().default(true),
    lastAssignedAt: instant("last_assigned_at"),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    uniqueIndex("judge_roster_user_unique").on(table.communityId, table.userId),
    // The roster picker is "searchable, most-recent first" (ux.md §3.5), which is this index.
    index("judge_roster_recent_idx").on(table.communityId, table.lastAssignedAt),
  ],
);

export const ASSIGNMENT_STATUSES = ["assigned", "active", "completed", "revoked"] as const;

/** Null `categoryId` assigns a judge across a whole competition rather than one category. */
export const judgeAssignments = sqliteTable(
  "judge_assignments",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    eventId: ref("event_id").notNull(),
    sessionId: ref("session_id").notNull(),
    competitionId: ref("competition_id").notNull(),
    categoryId: ref("category_id"),
    phaseId: ref("phase_id"),
    userId: ref("user_id").notNull(),
    /** The access code issued for this assignment, when the event uses codes rather than email. */
    credentialId: ref("credential_id"),
    assignedBy: ref("assigned_by"),
    assignedAt: instant("assigned_at").notNull(),
    revokedAt: instant("revoked_at"),
    status: text("status", { enum: ASSIGNMENT_STATUSES }).notNull().default("assigned"),
    ...audited(),
  },
  (table) => [
    // Partial: only *live* assignments are unique on this key. A revoked assignment stays as
    // history (its scores must survive), so without the `WHERE` a judge revoked from a category
    // could never be re-added there — the second insert collided with the tombstone and surfaced
    // as a 500. Re-adding is a real path ("we picked the wrong person, then changed our mind"), so
    // the slot is only occupied while `revoked_at IS NULL`.
    uniqueIndex("judge_assignments_unique")
      .on(table.sessionId, table.competitionId, table.categoryId, table.userId)
      .where(sql`${table.revokedAt} is null`),
    index("judge_assignments_session_idx").on(table.sessionId, table.competitionId),
    index("judge_assignments_user_idx").on(table.userId, table.status),
  ],
);

export const SCORE_STATUSES = ["draft", "submitted", "locked"] as const;

export const scores = sqliteTable(
  "scores",
  {
    /** Client-generated ULID. This is what makes submission idempotent under retry. */
    id: id(),
    communityId: ref("community_id").notNull(),
    judgeAssignmentId: ref("judge_assignment_id").notNull(),
    registrationEntryId: ref("registration_entry_id").notNull(),
    phaseId: ref("phase_id"),

    /** The judge's running total, as their device computed it with the same engine the server
     *  uses. Stored so a disagreement between device and server is detectable rather than
     *  invisible; the authoritative figure is always recomputed from the criteria values. */
    total: integer("total"),

    status: text("status", { enum: SCORE_STATUSES }).notNull().default("draft"),
    /** Incremented on every write. A submission carrying a stale version is a conflict, not a
     *  silent overwrite. */
    version: integer("version").notNull().default(1),
    deviceId: text("device_id"),
    submittedAt: instant("submitted_at"),
    syncedAt: instant("synced_at"),
    lockedAt: instant("locked_at"),
    ...audited(),
  },
  (table) => [
    uniqueIndex("scores_assignment_entry_unique").on(
      table.judgeAssignmentId,
      table.registrationEntryId,
      table.phaseId,
    ),
    index("scores_entry_idx").on(table.registrationEntryId),
  ],
);

export const scoreCriteriaValues = sqliteTable(
  "score_criteria_values",
  {
    id: id(),
    scoreId: ref("score_id").notNull(),
    criterionId: ref("criterion_id").notNull(),
    communityId: ref("community_id").notNull(),
    value: integer("value").notNull(),
    ...audited(),
  },
  (table) => [uniqueIndex("score_criteria_values_unique").on(table.scoreId, table.criterionId)],
);

/**
 * Abstention is a record, not a missing score.
 *
 * "This judge is related to this dancer" and "this judge forgot" must not look identical to the
 * tabulator — one is a legitimate reason to compute from four scores, the other is a gap that
 * should block approval.
 */
export const abstentions = sqliteTable(
  "abstentions",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    judgeAssignmentId: ref("judge_assignment_id").notNull(),
    registrationEntryId: ref("registration_entry_id").notNull(),
    phaseId: ref("phase_id"),
    reason: text("reason").notNull(),
    ...audited(),
  },
  (table) => [
    uniqueIndex("abstentions_unique").on(
      table.judgeAssignmentId,
      table.registrationEntryId,
      table.phaseId,
    ),
  ],
);

export const ADJUSTMENT_KINDS = ["penalty", "deduction", "bonus", "correction"] as const;

/**
 * A tabulator's adjustment to an entrant's computed total — a timing penalty, a protocol
 * deduction, a bonus, or the correction of an invalid judge score (D34).
 *
 * `points` is the signed delta the engine applies after aggregation, so a penalty of 5 is
 * stored as -5 and the arithmetic stays integer throughout (D31). The reason is NOT NULL
 * because an adjustment is exactly the "friction where money and fairness live" case (ux.md §2)
 * — there is no such thing as an unexplained penalty.
 */
export const scoreAdjustments = sqliteTable(
  "score_adjustments",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    registrationEntryId: ref("registration_entry_id").notNull(),
    categoryId: ref("category_id").notNull(),
    phaseId: ref("phase_id"),
    kind: text("kind", { enum: ADJUSTMENT_KINDS }).notNull(),
    label: text("label").notNull(),
    points: integer("points").notNull(),
    reason: text("reason").notNull(),
    appliedBy: ref("applied_by"),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    index("score_adjustments_entry_idx").on(table.registrationEntryId, table.phaseId),
    index("score_adjustments_category_idx").on(table.categoryId),
  ],
);

/**
 * The order entrants are called in a category. Read by the arena director's running-order view
 * and by the judge's "next dancer", so both are looking at the same sequence.
 */
export const runningOrder = sqliteTable(
  "running_order",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    categoryId: ref("category_id").notNull(),
    phaseId: ref("phase_id"),
    registrationEntryId: ref("registration_entry_id").notNull(),
    position: integer("position").notNull(),
    ...audited(),
  },
  (table) => [
    uniqueIndex("running_order_unique").on(
      table.categoryId,
      table.phaseId,
      table.registrationEntryId,
    ),
    index("running_order_position_idx").on(table.categoryId, table.position),
  ],
);
